import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { ActionSheetKeyboardAwareView } from './action-sheet-keyboard-aware-view';
import { AddCollectionContent } from './add-collection-content';
import { Button, Props as ButtonProps } from '@/components/button';
import {
  CollectionOptionType,
  useCollectionListOptions,
} from '@/hooks/use-collection-list-options';
import {
  CombinedActionSheet,
  JumpToFn,
  Route as RouteType,
} from '@/components/combined-action-sheet';
import { DeleteCollectionConfirmation } from './delete-collection-confirmation';
import { DuplicateCollectionContent } from './duplicate-collection-content';
import { EditableCollectionsList } from './editable-collections-list';
import { EditCollectionActionsContent } from './edit-collection-actions-content';
import {
  EditCollectionsActionType,
  useEditCollectionsReducer,
} from '@/hooks/use-edit-collections-reducer';
import { gql, useMutation } from '@apollo/client';
import {
  ItemsContext_OrderGuideRestaurantCollectionFieldsFragment,
  OrderGuide_SetCollectionSortOrderMutation,
  OrderGuide_SetCollectionSortOrderMutationVariables,
} from '@/types';
import { ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT } from '@/contexts/items-context-queries';
import { OrderGuideCollectionList } from './order-guide-collection-list';
import { RenameCollectionContent } from './rename-collection-content';
import { SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { TrackingData } from '@/utils/analytics/base-reporter';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOrderGuideStoreApi } from '@/stores/order-guide-store';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';

const RIGHT_ACTION_HIT_SLOP = {
  bottom: 0,
  left: 8,
  right: 8,
  top: 0,
};

export const SET_COLLECTION_SORT_ORDER_MUTATION = gql`
  mutation OrderGuide_SetCollectionSortOrder(
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $collectionUUIDList: [uuid!]!
  ) {
    sortOrderGuideCollections(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
      sorted_order_guide_collection_id_list: $collectionUUIDList
    ) {
      order_guide_restaurant_collection {
        ...ItemsContext_OrderGuideRestaurantCollectionFields
      }
    }
  }

  ${ORDER_GUIDE_RESTAURANT_COLLECTION_FRAGMENT}
`;

export enum Route {
  AddCollection = 'ADD_COLLECTION',
  DeleteCollection = 'DELETE_COLLECTION',
  DuplicateCollection = 'DUPLICATE_COLLECTION',
  EditCollectionActions = 'EDIT_COLLECTION_ACTIONS',
  EditCollections = 'EDIT_COLLECTIONS',
  RenameCollection = 'RENAME_COLLECTION',
  SelectCollection = 'SELECT_COLLECTION',
}

const routes: Record<Route, RouteType> = {
  [Route.DeleteCollection]: {
    previousKey: Route.EditCollectionActions,
  },
  [Route.DuplicateCollection]: {
    previousKey: Route.EditCollectionActions,
    showSmallLayoutBackButton: true,
    showSmallLayoutTitle: true,
    title: i18n.t('new_order_guide'),
  },
  [Route.AddCollection]: {
    previousKey: Route.SelectCollection,
    showSmallLayoutBackButton: true,
    showSmallLayoutTitle: true,
    title: i18n.t('new_order_guide'),
  },
  [Route.SelectCollection]: {
    previousKey: null,
    showSmallLayoutTitle: false,
    title: i18n.t('order_guides'),
  },
  [Route.EditCollections]: {
    previousKey: null,
    showSmallLayoutTitle: false,
    title: i18n.t('edit_order_guides'),
  },
  [Route.EditCollectionActions]: {
    previousKey: Route.EditCollections,
  },
  [Route.RenameCollection]: {
    previousKey: Route.EditCollectionActions,
    showSmallLayoutBackButton: true,
    showSmallLayoutTitle: true,
    title: i18n.t('edit_order_guide_name'),
  },
};

export interface Props {
  canEditCollections?: boolean;
  initialRoute?: Route;
  onClose: () => void;
  onSelect: (collectionUUID: string) => void;
  selectedCollectionUUID: string;
}

const DONE_BUTTON_TRACKING_DATA: TrackingData = {
  name: 'edit_collections_list.done_button',
};
const EDIT_BUTTON_TRACKING_DATA: TrackingData = {
  name: 'collections_list.edit_button',
};

export const OrderGuideCollectionsActionSheet: React.FC<Props> = ({
  canEditCollections = true,
  initialRoute = Route.SelectCollection,
  onClose,
  onSelect,
  selectedCollectionUUID,
}) => {
  const [inputError, setInputError] = useState('');
  // This doesn't control sort order, it's just used to keep track of changes
  // Use ref instead of state so CombinedActionSheet doesn't re-render on change
  const { isSmallLayout } = useLayoutContext();
  const { height } = useWindowDimensions();
  const orderGuideStoreApi = useOrderGuideStoreApi();
  const { showErrorToast } = useToasts();
  const collectionListData = useCollectionListOptions();
  const [collectionToEdit, setCollectionToEdit] =
    useState<CollectionOptionType | null>(null);
  const [state, dispatch] = useEditCollectionsReducer(collectionListData);

  const {
    selectedRestaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useUserContext();

  const [setCollectionSortOrder, { loading }] = useMutation<
    OrderGuide_SetCollectionSortOrderMutation,
    OrderGuide_SetCollectionSortOrderMutationVariables
  >(SET_COLLECTION_SORT_ORDER_MUTATION);

  const actionSheetMaxHeight = Math.floor(height * 0.85);

  const resolvedInitialRoute = canEditCollections
    ? initialRoute
    : Route.SelectCollection;

  const onDonePress = async (jumpTo: JumpToFn<Route>) => {
    try {
      if (state.sortOrderChanged) {
        const result = await setCollectionSortOrder({
          variables: {
            collectionUUIDList: state.collections.map((c) => String(c.value)),
            restaurantUUID: selectedRestaurantUUID,
            supplierUUID,
          },
        });
        const responseData = result.data;
        if (!responseData) {
          return;
        }
        const sortedCollections = responseData.sortOrderGuideCollections.reduce<
          ItemsContext_OrderGuideRestaurantCollectionFieldsFragment[]
        >((acc, entry) => {
          const collection = entry.order_guide_restaurant_collection;
          if (collection) {
            acc.push(collection);
          }
          return acc;
        }, []);
        orderGuideStoreApi
          .getState()
          .setRestaurantCollections(sortedCollections);
        dispatch({ type: EditCollectionsActionType.RESET_SORT_ORDER_CHANGED });
      }
      jumpTo(Route.SelectCollection);
    } catch (e) {
      showErrorToast();
    }
  };

  const sharedRightButtonProps: Partial<ButtonProps> = {
    hitSlop: RIGHT_ACTION_HIT_SLOP,
    size: 'small',
    style: styles.rightActionButton,
    type: 'tertiary',
  };

  return (
    <CombinedActionSheet
      contentStyle={[styles.base, { maxHeight: actionSheetMaxHeight }]}
      hasStickyHeader={false}
      initialRoute={resolvedInitialRoute}
      onClose={onClose}
      onRouteChange={(nextKey) => {
        if (
          nextKey === Route.SelectCollection ||
          nextKey === Route.EditCollections
        ) {
          setCollectionToEdit(null);
        }
        setInputError('');
      }}
      renderScene={({ jumpTo, routeKey }) => {
        switch (routeKey) {
          case Route.AddCollection:
            return (
              <ActionSheetKeyboardAwareView
                style={styles.actionSheetInputContainer}
              >
                <AddCollectionContent
                  inputError={inputError}
                  onCreate={onSelect}
                  saveButtonText={i18n.t('create')}
                  setInputError={setInputError}
                />
              </ActionSheetKeyboardAwareView>
            );
          case Route.SelectCollection:
            return (
              <>
                {isSmallLayout ? (
                  <View style={[styles.reapplyPadding, styles.listHeader]}>
                    <Text style={TYPOGRAPHY.h2}>{i18n.t('order_guides')}</Text>
                    {canEditCollections ? (
                      <Button
                        key="right-action-edit"
                        {...sharedRightButtonProps}
                        onPress={() => {
                          jumpTo(Route.EditCollections);
                        }}
                        trackingData={EDIT_BUTTON_TRACKING_DATA}
                      >
                        {i18n.t('edit')}
                      </Button>
                    ) : null}
                  </View>
                ) : null}
                <OrderGuideCollectionList
                  collectionData={state.collections}
                  onCreate={
                    canEditCollections
                      ? () => jumpTo(Route.AddCollection)
                      : undefined
                  }
                  onSelect={onSelect}
                  selectedCollectionUUID={selectedCollectionUUID}
                />
                {isSmallLayout || !canEditCollections ? null : (
                  <Button
                    containerStyle={styles.actionButtonContainer}
                    onPress={() => {
                      jumpTo(Route.EditCollections);
                    }}
                    trackingData={EDIT_BUTTON_TRACKING_DATA}
                    type="secondary"
                  >
                    {i18n.t('edit')}
                  </Button>
                )}
              </>
            );
          case Route.EditCollections:
            return (
              <>
                {isSmallLayout ? (
                  <View style={[styles.reapplyPadding, styles.listHeader]}>
                    <Text style={TYPOGRAPHY.h2}>
                      {i18n.t('edit_order_guides')}
                    </Text>
                    <Button
                      key="right-action-done"
                      {...sharedRightButtonProps}
                      loading={loading}
                      onPress={async () => {
                        await onDonePress(jumpTo);
                      }}
                      trackingData={DONE_BUTTON_TRACKING_DATA}
                    >
                      {i18n.t('done')}
                    </Button>
                  </View>
                ) : null}
                <EditableCollectionsList
                  collectionData={state.collections}
                  onCollectionOrderChange={(sortedCollections) => {
                    dispatch({
                      payload: {
                        collections: sortedCollections,
                      },
                      type: EditCollectionsActionType.SORT_COLLECTIONS,
                    });
                  }}
                  onEditPress={(collection) => {
                    setCollectionToEdit(collection);
                    jumpTo(Route.EditCollectionActions);
                  }}
                />
                {isSmallLayout ? null : (
                  <Button
                    containerStyle={styles.actionButtonContainer}
                    loading={loading}
                    onPress={async () => {
                      await onDonePress(jumpTo);
                    }}
                    trackingData={DONE_BUTTON_TRACKING_DATA}
                  >
                    {i18n.t('done')}
                  </Button>
                )}
              </>
            );
          case Route.EditCollectionActions:
            if (collectionToEdit) {
              return (
                <EditCollectionActionsContent
                  collection={collectionToEdit}
                  goBack={() => jumpTo(Route.EditCollections)}
                  onDeletePress={() => jumpTo(Route.DeleteCollection)}
                  onDuplicatePress={() => jumpTo(Route.DuplicateCollection)}
                  onRenamePress={() => jumpTo(Route.RenameCollection)}
                />
              );
            }
            return null;
          case Route.DuplicateCollection:
            if (!collectionToEdit) {
              return null;
            }
            return (
              <ActionSheetKeyboardAwareView style={styles.reapplyPadding}>
                <DuplicateCollectionContent
                  inputError={inputError}
                  onDuplicate={(newCollection) => {
                    dispatch({
                      payload: {
                        newCollection,
                      },
                      type: EditCollectionsActionType.ADD_COLLECTION,
                    });
                    jumpTo(Route.EditCollections);
                  }}
                  setInputError={setInputError}
                  sourceCollection={collectionToEdit}
                />
              </ActionSheetKeyboardAwareView>
            );
          case Route.DeleteCollection:
            if (collectionToEdit) {
              return (
                <View style={styles.reapplyPadding}>
                  <DeleteCollectionConfirmation
                    collectionName={collectionToEdit.title}
                    collectionUUID={String(collectionToEdit.value)}
                    deleteCollectionMode="delete"
                    onCancel={() => jumpTo(Route.EditCollectionActions)}
                    onDelete={() => {
                      dispatch({
                        payload: {
                          collectionUUID: String(collectionToEdit.value),
                        },
                        type: EditCollectionsActionType.REMOVE_COLLECTION,
                      });
                      jumpTo(Route.EditCollections);
                    }}
                  />
                </View>
              );
            }
            return null;
          case Route.RenameCollection:
            if (!collectionToEdit) {
              return null;
            }
            return (
              <ActionSheetKeyboardAwareView style={styles.reapplyPadding}>
                <RenameCollectionContent
                  collections={collectionListData}
                  onSave={(newName) => {
                    dispatch({
                      payload: {
                        collectionUUID: String(collectionToEdit.value),
                        name: newName,
                      },
                      type: EditCollectionsActionType.RENAME_COLLECTION,
                    });
                    jumpTo(Route.EditCollections);
                  }}
                  selectedCollection={collectionToEdit}
                />
              </ActionSheetKeyboardAwareView>
            );
        }
      }}
      routes={routes}
      titleStyle={styles.reapplyPadding}
    />
  );
};

const styles = StyleSheet.create({
  actionButtonContainer: {
    marginHorizontal: SCREEN_PADDING,
    marginTop: 8,
  },
  actionSheetInputContainer: {
    paddingHorizontal: SCREEN_PADDING,
  },
  base: {
    paddingHorizontal: 0,
  },
  listHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  reapplyPadding: {
    paddingHorizontal: SCREEN_PADDING,
  },
  rightActionButton: {
    borderWidth: 0,
    paddingHorizontal: 0,
  },
});
